import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AutoComplete, Button, DatePicker, Input, InputNumber, Segmented, Select, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { ArrowLeft, ListRestart, Plus, Trash2 } from 'lucide-react';
import Layout from '../../Layout';
import { getCallCenter } from '../CustomerDemo/callCenterStore';
import { PRACTITIONERS } from './khoDemo';
import { khoApi, newLineId, useKho, type DocDraft } from './khoStore';
import {
  DISPOSE_REASONS, DOC_TYPE, ITEM_KIND, NO_LOT, allocateFefo, docById, docWarnings, expiryLevel, fmtMoney, fmtNum, fmtQty, itemById, lotsFor,
  normCost, reservedQty, validateDoc, type DocLine, type DocType, type Item, type KhoState, type StockDoc,
} from './khoLogic';
import { PageHeader, Panel, docUrl, useKhoPerms } from './khoUi';

const AUTO = '__auto';
const EXPORT_TYPES: DocType[] = ['issue_service', 'issue_sale', 'transfer', 'dispose'];

interface FormLine {
  key: string;
  itemId?: string;
  /** Xuất: số lô hoặc AUTO (FEFO) */
  lotNo: string;
  expiry?: string;
  qty: number | null;
  unit: 'base' | 'pack';
  /** Nhập: đơn giá theo đơn vị đang chọn */
  price: number | null;
  docQty: number | null;
}

interface Header {
  type: DocType;
  date: Dayjs;
  warehouseId?: string;
  toWarehouseId?: string;
  supplierId?: string;
  refCode: string;
  serviceId?: string;
  practitioner?: string;
  customerName: string;
  customerPhone: string;
  reason?: string;
  note: string;
}

const emptyLine = (type: DocType): FormLine => ({ key: newLineId(), lotNo: type === 'import' ? '' : AUTO, qty: null, unit: 'base', price: null, docQty: null });
const factor = (item: Item | null, unit: FormLine['unit']) => (unit === 'pack' && item?.packSize ? item.packSize : 1);

const defaultWarehouse = (s: KhoState, type: DocType) => {
  const kind = type === 'issue_service' ? 'room' : type === 'issue_sale' ? 'retail' : 'central';
  return (s.warehouses.find(w => w.kind === kind) || s.warehouses[0])?.id;
};

/** Chuyển dòng nhập trên form thành dòng phiếu (xuất "tự động" được chia lô theo FEFO) */
function buildLines(s: KhoState, h: Header, lines: FormLine[], docId: string | undefined, now: number): { lines: DocLine[]; short: string[] } {
  const out: DocLine[] = [];
  const short: string[] = [];
  for (const l of lines) {
    const item = itemById(s, l.itemId);
    if (!item || !l.qty) continue;
    const qty = l.qty * factor(item, l.unit);
    if (h.type === 'import') {
      out.push({ id: l.key, itemId: item.id, lotNo: l.lotNo.trim(), expiry: l.expiry, qty, unitCost: (l.price ?? item.cost * factor(item, l.unit)) / factor(item, l.unit), docQty: l.docQty === null ? undefined : l.docQty * factor(item, l.unit) });
    } else if (l.lotNo === AUTO) {
      if (!h.warehouseId) continue;
      const a = allocateFefo(s, h.warehouseId, item.id, qty, { now, allowExpired: h.type === 'dispose', exceptDocId: docId });
      a.lines.forEach((x, i) => out.push({ ...x, id: `${l.key}-${i}` }));
      if (a.short > 0) short.push(`${item.name}: thiếu ${fmtQty(item, a.short)} so với tồn có thể xuất`);
    } else {
      const lot = h.warehouseId ? lotsFor(s, h.warehouseId, item.id).find(x => x.lotNo === l.lotNo) : undefined;
      out.push({ id: l.key, itemId: item.id, lotNo: l.lotNo, expiry: lot?.expiry ?? l.expiry, qty, unitCost: lot?.unitCost ?? item.cost });
    }
  }
  return { lines: out, short };
}

/** Lập / sửa phiếu nhập mua và các phiếu xuất (dịch vụ, bán, chuyển kho, hủy) */
/** Đổi query (mở phiếu khác / loại khác trên cùng đường dẫn) thì dựng lại form từ đầu */
export default function DocFormPage({ mode }: { mode: 'import' | 'export' }) {
  const { search } = useLocation();
  return <DocForm key={search} mode={mode} />;
}

function DocForm({ mode }: { mode: 'import' | 'export' }) {
  const s = useKho();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { canCreate, me } = useKhoPerms();
  // Lưu nháp xong mà gửi duyệt lỗi: giữ nguyên form, các lần lưu sau cập nhật đúng phiếu đó
  const [savedId, setSavedId] = useState<string>();
  const editId = params.get('id') || savedId;
  const editing = editId ? docById(s, editId) : null;
  const now = Date.now();

  const initType: DocType = editing?.type || (mode === 'import' ? 'import' : (EXPORT_TYPES.includes(params.get('type') as DocType) ? (params.get('type') as DocType) : 'issue_service'));
  const [h, setH] = useState<Header>(() => initHeader(s, initType, editing, params));
  const [lines, setLines] = useState<FormLine[]>(() => initLines(s, initType, editing, params));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const patch = (p: Partial<Header>) => setH(x => ({ ...x, ...p }));
  const setLine = (key: string, p: Partial<FormLine>) => setLines(ls => ls.map(l => (l.key === key ? { ...l, ...p } : l)));

  // Chọn dịch vụ khi chưa có dòng nào → tự điền theo định mức
  useEffect(() => {
    if (h.type === 'issue_service' && h.serviceId && !lines.some(l => l.itemId)) fillNorm(h.serviceId);
  }, [h.serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fillNorm = (serviceId: string) => {
    const norm = s.norms[serviceId] || [];
    setLines(norm.length ? norm.map(n => ({ ...emptyLine('issue_service'), itemId: n.itemId, qty: n.qty })) : [emptyLine('issue_service')]);
  };

  const built = useMemo(() => buildLines(s, h, lines, editId, now), [s, h, lines, editId]); // eslint-disable-line react-hooks/exhaustive-deps
  const preview: StockDoc = {
    id: editId || '__new', code: '', type: h.type, status: 'draft', date: h.date.valueOf(), warehouseId: h.warehouseId || '', toWarehouseId: h.toWarehouseId,
    supplierId: h.supplierId, serviceId: h.serviceId, reason: h.reason, lines: built.lines, createdBy: me, createdAt: now, log: [],
    customer: h.customerName.trim() ? { name: h.customerName.trim(), phone: h.customerPhone.trim() } : undefined,
  };
  const warnings = built.lines.length ? docWarnings(s, preview, now) : [];
  const total = built.lines.reduce((t, l) => t + l.qty * l.unitCost, 0);
  const retail = h.type === 'issue_sale' ? built.lines.reduce((t, l) => t + l.qty * (itemById(s, l.itemId)?.price || 0), 0) : 0;

  if (editId && (!editing || (editing.status !== 'draft' && editing.status !== 'rejected'))) {
    return (
      <Layout>
        <Alert type="warning" showIcon message={editing ? `Phiếu ${editing.code} đã ${editing.status === 'approved' ? 'duyệt' : 'gửi duyệt / hủy'}, không sửa được nữa.` : 'Không tìm thấy phiếu.'}
          action={editing ? <Link to={docUrl(editing.id)}>Xem phiếu</Link> : <Link to="/import-export">Về danh sách</Link>} />
      </Layout>
    );
  }
  if (!canCreate) {
    return <Layout><Alert type="warning" showIcon message="Bạn không có quyền lập phiếu kho." /></Layout>;
  }

  const save = (submit: boolean) => {
    const errs = [...built.short];
    if (!built.lines.length) errs.push('Thêm ít nhất một mặt hàng có số lượng');
    if (h.type === 'import') lines.filter(l => l.itemId && l.qty).forEach(l => { if (!l.lotNo.trim()) errs.push(`${itemById(s, l.itemId)?.name}: nhập số lô`); });
    if (submit) errs.push(...validateDoc(s, preview, now));
    const uniq = Array.from(new Set(errs));
    if (uniq.length) { setErrors(uniq); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setBusy(true);
    const at = h.date.isSame(now, 'day') ? now : h.date.hour(12).minute(0).valueOf();
    const draft: DocDraft = {
      id: editId, type: h.type, date: at, warehouseId: h.warehouseId!, lines: built.lines,
      ...(h.type === 'transfer' ? { toWarehouseId: h.toWarehouseId } : {}),
      ...(h.type === 'import' ? { supplierId: h.supplierId, refCode: h.refCode.trim() || undefined } : {}),
      ...(h.type === 'issue_service' ? { serviceId: h.serviceId, practitioner: h.practitioner } : {}),
      ...((h.type === 'issue_service' || h.type === 'issue_sale') && h.customerName.trim() ? { customer: { name: h.customerName.trim(), phone: h.customerPhone.trim() } } : {}),
      ...(h.type === 'dispose' ? { reason: h.reason } : {}),
      note: h.note.trim() || undefined,
    };
    const r = khoApi.saveDoc(draft, me);
    if (!r.ok || !r.id) { setBusy(false); setErrors(r.errors || ['Không lưu được']); return; }
    if (submit) {
      const sub = khoApi.submitDoc(r.id, me);
      if (!sub.ok) { setBusy(false); setErrors(sub.errors || []); setSavedId(r.id); return; }
      message.success('Đã gửi duyệt phiếu');
    } else message.success('Đã lưu nháp');
    navigate(docUrl(r.id));
  };

  const whOptions = s.warehouses.map(w => ({ value: w.id, label: w.name }));
  const itemOptions = s.items
    .filter(i => i.active || lines.some(l => l.itemId === i.id))
    .sort((a, b) => (h.type === 'issue_sale' ? Number(b.kind === 'cosmetic') - Number(a.kind === 'cosmetic') : 0) || a.code.localeCompare(b.code))
    .map(i => ({ value: i.id, label: `${i.code} · ${i.name}`, kind: ITEM_KIND[i.kind].label }));
  const customers = getCallCenter().customers.filter(c => c.phones[0]);
  const custOptions = customers
    .filter(c => !h.customerName || c.name.toLowerCase().includes(h.customerName.toLowerCase()) || c.phones[0].includes(h.customerName))
    .slice(0, 8)
    .map(c => ({ value: `${c.id}`, label: `${c.name} · ${c.phones[0]}` }));
  const isImport = h.type === 'import';
  const field = (label: string, node: React.ReactNode, required = false, wide = false) => (
    <div className={wide ? 'md:col-span-2 xl:col-span-3' : ''}>
      <label className="text-xs text-gray-500 mb-1 block">{label}{required && <span className="text-rose-500"> *</span>}</label>
      {node}
    </div>
  );

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader
          title={editing ? `Sửa phiếu ${editing.code}` : `Lập phiếu ${DOC_TYPE[h.type].label.toLowerCase()}`}
          desc={isImport ? 'Nhập hàng mua từ nhà cung cấp — bắt buộc số lô và hạn dùng, đối chiếu số thực nhận với chứng từ'
            : h.type === 'issue_service' ? 'Xuất vật tư cho một buổi dịch vụ — tự điền theo định mức, lô hạn gần nhất xuất trước'
              : h.type === 'transfer' ? 'Chuyển hàng giữa các kho — kho nhận xác nhận thì hàng mới vào kho'
                : h.type === 'dispose' ? 'Hủy hàng hết hạn, hỏng vỡ… — tồn chỉ giảm khi phiếu được duyệt'
                  : 'Xuất bán mỹ phẩm cho khách tại quầy'}
          extra={<Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>Quay lại</Button>} />

        {!editing && mode === 'export' && (
          <Segmented value={h.type} className="self-start"
            onChange={v => { const t = v as DocType; setH(x => ({ ...x, type: t, warehouseId: defaultWarehouse(s, t) })); setLines([emptyLine(t)]); setErrors([]); }}
            options={EXPORT_TYPES.map(t => ({ value: t, label: DOC_TYPE[t].label }))} />
        )}

        {editing?.status === 'rejected' && <Alert type="error" showIcon message={`Phiếu bị từ chối: ${editing.rejectReason || ''}`} description="Sửa lại rồi gửi duyệt lần nữa." />}
        {errors.length > 0 && <Alert type="error" showIcon closable onClose={() => setErrors([])} message="Chưa lưu được phiếu" description={<ul className="list-disc pl-4 m-0">{errors.map(e => <li key={e}>{e}</li>)}</ul>} />}

        <Panel title="Thông tin phiếu">
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {field('Ngày chứng từ', <DatePicker className="w-full" format="DD/MM/YYYY" value={h.date} allowClear={false} onChange={d => d && patch({ date: d })} disabledDate={d => d.isAfter(dayjs(), 'day')} />, true)}
            {field(isImport ? 'Nhập vào kho' : h.type === 'transfer' ? 'Kho xuất' : 'Xuất từ kho',
              <Select className="w-full" value={h.warehouseId} options={whOptions} onChange={v => { patch({ warehouseId: v }); if (!isImport) setLines(ls => ls.map(l => ({ ...l, lotNo: AUTO }))); }} />, true)}
            {h.type === 'transfer' && field('Kho nhận', <Select className="w-full" value={h.toWarehouseId} placeholder="Chọn kho nhận" options={whOptions.filter(o => o.value !== h.warehouseId)} onChange={v => patch({ toWarehouseId: v })} />, true)}
            {isImport && field('Nhà cung cấp', <Select className="w-full" showSearch optionFilterProp="label" placeholder="Chọn nhà cung cấp" value={h.supplierId} onChange={v => patch({ supplierId: v })}
              options={s.suppliers.map(x => ({ value: x.id, label: x.name }))} />, true)}
            {isImport && field('Số hóa đơn / đơn mua', <Input value={h.refCode} placeholder="VD: HD-0012345" onChange={e => patch({ refCode: e.target.value })} />)}
            {h.type === 'issue_service' && field('Dịch vụ', (
              <div className="flex gap-2">
                <Select className="flex-1 min-w-0" showSearch optionFilterProp="label" placeholder="Chọn dịch vụ" value={h.serviceId} onChange={v => patch({ serviceId: v })}
                  options={s.services.map(x => ({ value: x.id, label: x.name }))} />
                {h.serviceId && <Button icon={<ListRestart size={14} />} title="Điền lại theo định mức" onClick={() => fillNorm(h.serviceId!)}>Định mức</Button>}
              </div>
            ), true)}
            {h.type === 'issue_service' && field('Người thực hiện', <Select className="w-full" allowClear placeholder="Bác sĩ / KTV" value={h.practitioner} onChange={v => patch({ practitioner: v })}
              options={PRACTITIONERS.map(p => ({ value: p, label: p }))} />)}
            {(h.type === 'issue_service' || h.type === 'issue_sale') && field('Khách hàng', (
              <AutoComplete className="w-full" value={h.customerName} options={custOptions} placeholder="Tên hoặc số điện thoại khách"
                onChange={v => patch({ customerName: v })}
                onSelect={(v: string) => { const c = customers.find(x => `${x.id}` === v); if (c) patch({ customerName: c.name, customerPhone: c.phones[0] }); }} />
            ), h.type === 'issue_service')}
            {(h.type === 'issue_service' || h.type === 'issue_sale') && field('Số điện thoại khách', <Input value={h.customerPhone} onChange={e => patch({ customerPhone: e.target.value })} />)}
            {h.type === 'dispose' && field('Lý do hủy', <Select className="w-full" placeholder="Chọn lý do" value={h.reason} onChange={v => patch({ reason: v })} options={DISPOSE_REASONS.map(r => ({ value: r, label: r }))} />, true)}
            {field('Ghi chú', <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} value={h.note} onChange={e => patch({ note: e.target.value })} placeholder={h.type === 'dispose' ? 'Mô tả sự việc, người phát hiện…' : 'Ghi chú thêm'} />, false, true)}
          </div>
        </Panel>

        <Panel title={`Hàng hóa (${lines.filter(l => l.itemId).length})`}
          extra={h.type === 'issue_service' && h.serviceId ? <span className="text-xs text-gray-500">Chi phí định mức / buổi: <b>{fmtMoney(normCost(s, h.serviceId))}</b></span> : undefined}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/60">
                <tr>
                  <th className="text-left font-medium px-3 py-2 w-8">#</th>
                  <th className="text-left font-medium px-3 py-2">Mặt hàng</th>
                  <th className="text-left font-medium px-3 py-2 w-[210px]">{isImport ? 'Số lô · Hạn dùng' : 'Lô xuất'}</th>
                  {isImport && <th className="text-right font-medium px-3 py-2 w-[110px]">SL chứng từ</th>}
                  <th className="text-right font-medium px-3 py-2 w-[200px]">{isImport ? 'SL thực nhận' : 'Số lượng'}</th>
                  {isImport && <th className="text-right font-medium px-3 py-2 w-[150px]">Đơn giá</th>}
                  <th className="text-right font-medium px-3 py-2 w-[130px]">{isImport ? 'Thành tiền' : 'Giá vốn'}</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <LineRow key={l.key} s={s} h={h} l={l} idx={i} docId={editId} now={now} itemOptions={itemOptions}
                    onChange={p => setLine(l.key, p)} onRemove={() => setLines(ls => (ls.length > 1 ? ls.filter(x => x.key !== l.key) : [emptyLine(h.type)]))} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-800">
            <Button icon={<Plus size={14} />} onClick={() => setLines(ls => [...ls, emptyLine(h.type)])}>Thêm dòng</Button>
            <div className="text-sm text-right">
              <div>{isImport ? 'Tổng tiền hàng' : 'Tổng giá vốn'}: <b className="text-base">{fmtMoney(total)}</b></div>
              {h.type === 'issue_sale' && retail > 0 && <div className="text-xs text-gray-500">Giá bán lẻ: {fmtMoney(retail)} · lãi gộp {fmtMoney(retail - total)}</div>}
            </div>
          </div>
        </Panel>

        {warnings.length > 0 && <Alert type="warning" showIcon message="Lưu ý" description={<ul className="list-disc pl-4 m-0">{warnings.map(w => <li key={w}>{w}</li>)}</ul>} />}

        <div className="sticky bottom-0 z-20 bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm px-4 py-3 flex justify-end gap-2">
          <Button onClick={() => navigate(-1)}>Hủy bỏ</Button>
          <Button loading={busy} onClick={() => save(false)}>Lưu nháp</Button>
          <Button type="primary" loading={busy} onClick={() => save(true)}>Lưu & gửi duyệt</Button>
        </div>
      </div>
    </Layout>
  );
}

function LineRow({ s, h, l, idx, docId, now, itemOptions, onChange, onRemove }: {
  s: KhoState; h: Header; l: FormLine; idx: number; docId?: string; now: number;
  itemOptions: { value: string; label: string; kind: string }[];
  onChange: (p: Partial<FormLine>) => void; onRemove: () => void;
}) {
  const item = itemById(s, l.itemId);
  const isImport = h.type === 'import';
  const f = factor(item, l.unit);
  const base = (l.qty || 0) * f;
  const lots = item && h.warehouseId && !isImport ? lotsFor(s, h.warehouseId, item.id) : [];
  const free = (lotNo?: string) => {
    if (!item || !h.warehouseId) return 0;
    const pool = lotNo ? lots.filter(x => x.lotNo === lotNo) : lots.filter(x => h.type === 'dispose' || expiryLevel(x.expiry, now) !== 'expired');
    return pool.reduce((t, x) => t + x.qty - reservedQty(s, h.warehouseId!, item.id, x.lotNo, docId), 0);
  };
  const avail = !isImport && item ? free(l.lotNo === AUTO ? undefined : l.lotNo) : 0;
  const over = !isImport && item && base > avail + 1e-9;
  const unitCost = isImport ? (l.price ?? 0) / f : (lots.find(x => x.lotNo === l.lotNo) || lots[0])?.unitCost ?? item?.cost ?? 0;
  const diff = isImport && l.docQty !== null && l.qty !== null ? l.qty - l.docQty : 0;
  const unitSelect = item?.packUnit && item.packSize && item.packSize > 1 ? (
    <Select size="middle" className="!w-[96px]" value={l.unit} onChange={u => onChange({ unit: u, price: isImport && item ? (u === 'pack' ? item.cost * item.packSize! : item.cost) : l.price })}
      options={[{ value: 'base', label: item.unit }, { value: 'pack', label: item.packUnit }]} />
  ) : <span className="text-xs text-gray-500 w-[60px] inline-block">{item?.unit}</span>;

  return (
    <tr className="border-t border-gray-100 dark:border-gray-800 align-top">
      <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
      <td className="px-3 py-2">
        <Select className="w-full" showSearch optionFilterProp="label" placeholder="Chọn mặt hàng" value={l.itemId}
          onChange={v => { const it = itemById(s, v); onChange({ itemId: v, unit: 'base', lotNo: isImport ? l.lotNo : AUTO, price: isImport && it ? it.cost : null }); }}
          options={itemOptions} optionRender={o => <div className="flex justify-between gap-2"><span className="truncate">{o.label}</span><span className="text-[11px] text-gray-400 shrink-0">{(o.data as { kind: string }).kind}</span></div>} />
        {item && (item.cold || item.controlled) && (
          <div className="text-[11px] mt-1 flex gap-2">
            {item.cold && <span className="text-sky-600">❄ Bảo quản lạnh 2–8°C</span>}
            {item.controlled && <span className="text-violet-600">Thuốc kiểm soát</span>}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        {isImport ? (
          <div className="flex flex-col gap-1">
            <Input placeholder="Số lô" value={l.lotNo} onChange={e => onChange({ lotNo: e.target.value })} status={item && l.qty && !l.lotNo.trim() ? 'error' : undefined} />
            {item?.kind !== 'tool' && <DatePicker placeholder="Hạn sử dụng" format="DD/MM/YYYY" value={l.expiry ? dayjs(l.expiry) : null}
              onChange={d => onChange({ expiry: d ? d.format('YYYY-MM-DD') : undefined })} status={item && l.qty && !l.expiry ? 'error' : undefined} />}
          </div>
        ) : (
          <Select className="w-full" value={l.lotNo} disabled={!item} onChange={v => onChange({ lotNo: v })}
            options={[
              { value: AUTO, label: 'Tự động (hạn gần nhất trước)' },
              ...lots.map(x => {
                const lv = expiryLevel(x.expiry, now);
                return {
                  value: x.lotNo, disabled: lv === 'expired' && h.type !== 'dispose',
                  label: `${x.lotNo === NO_LOT ? 'Không lô' : x.lotNo}${x.expiry ? ` · HSD ${dayjs(x.expiry).format('DD/MM/YY')}` : ''} · còn ${fmtNum(x.qty)}${lv === 'expired' ? ' · HẾT HẠN' : ''}`,
                };
              }),
            ]} />
        )}
      </td>
      {isImport && (
        <td className="px-3 py-2 text-right">
          <InputNumber className="!w-full" min={0} value={l.docQty} onChange={v => onChange({ docQty: v })} placeholder="SL" />
        </td>
      )}
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-1">
          <InputNumber className="!w-[100px]" min={0} value={l.qty} status={over ? 'error' : undefined} onChange={v => onChange({ qty: v })} />
          {unitSelect}
        </div>
        {item && f > 1 && !!l.qty && <div className="text-[11px] text-gray-400 mt-0.5">= {fmtNum(base)} {item.unit}</div>}
        {!isImport && item && h.warehouseId && (
          <div className={`text-[11px] mt-0.5 ${over ? 'text-rose-600 font-medium' : 'text-gray-400'}`}>Có thể xuất: {fmtQty(item, Math.max(0, avail))}</div>
        )}
        {isImport && diff !== 0 && <div className={`text-[11px] mt-0.5 ${diff < 0 ? 'text-rose-600' : 'text-amber-600'}`}>{diff > 0 ? 'Thừa' : 'Thiếu'} {fmtNum(Math.abs(diff))} so với chứng từ</div>}
      </td>
      {isImport && (
        <td className="px-3 py-2 text-right">
          <InputNumber className="!w-full" min={0} step={1000} value={l.price} onChange={v => onChange({ price: v })}
            formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number((v || '').replace(/\./g, ''))} />
          {item && <div className="text-[11px] text-gray-400 mt-0.5">/ {l.unit === 'pack' ? item.packUnit : item.unit}</div>}
        </td>
      )}
      <td className="px-3 py-2 text-right whitespace-nowrap">{item && l.qty ? fmtMoney(base * unitCost) : <span className="text-gray-300">—</span>}</td>
      <td className="px-2 py-2 text-center">
        <button type="button" onClick={onRemove} className="text-gray-400 hover:text-rose-600 p-1" aria-label="Xóa dòng"><Trash2 size={15} /></button>
      </td>
    </tr>
  );
}

function initHeader(s: KhoState, type: DocType, d: StockDoc | null, params: URLSearchParams): Header {
  if (d) {
    return {
      type: d.type, date: dayjs(d.date), warehouseId: d.warehouseId, toWarehouseId: d.toWarehouseId, supplierId: d.supplierId, refCode: d.refCode || '',
      serviceId: d.serviceId, practitioner: d.practitioner, customerName: d.customer?.name || '', customerPhone: d.customer?.phone || '', reason: d.reason, note: d.note || '',
    };
  }
  const wh = params.get('wh');
  const items = (params.get('items') || '').split(',').map(x => x.split(':')[0]).filter(Boolean);
  return {
    type, date: dayjs(),
    warehouseId: wh && s.warehouses.some(w => w.id === wh) ? wh : defaultWarehouse(s, type),
    toWarehouseId: params.get('to') || (type === 'transfer' ? s.warehouses.find(w => w.kind === 'room')?.id : undefined),
    supplierId: params.get('supplier') || (items.length ? itemById(s, items[0])?.supplierId : undefined),
    refCode: '', serviceId: params.get('service') || undefined, practitioner: undefined,
    customerName: params.get('customer') || '', customerPhone: params.get('phone') || '', reason: params.get('reason') || undefined, note: '',
  };
}

function initLines(s: KhoState, type: DocType, d: StockDoc | null, params: URLSearchParams): FormLine[] {
  if (d) {
    return d.lines.length ? d.lines.map(l => ({
      key: l.id, itemId: l.itemId, lotNo: l.lotNo, expiry: l.expiry, qty: l.qty, unit: 'base' as const,
      price: d.type === 'import' ? l.unitCost : null, docQty: l.docQty ?? null,
    })) : [emptyLine(type)];
  }
  // Đặt hàng từ cảnh báo sắp hết: items=it-a:200,it-b:40 → điền sẵn theo đơn vị đóng gói
  const items = (params.get('items') || '').split(',').filter(Boolean);
  if (items.length) {
    return items.map(x => {
      const [id, q] = x.split(':');
      const it = itemById(s, id);
      const qty = Number(q) || 0;
      const pack = !!it?.packSize && it.packSize > 1 && qty % it.packSize === 0;
      const n = pack ? qty / it!.packSize! : qty;
      return { ...emptyLine(type), itemId: it?.id, qty: n || null, docQty: n || null, unit: pack ? 'pack' as const : 'base' as const, price: it ? it.cost * (pack ? it.packSize! : 1) : null };
    });
  }
  const item = params.get('item');
  if (item && itemById(s, item)) {
    const lot = params.get('lot');
    const wh = params.get('wh');
    const qty = Number(params.get('qty')) || (lot && wh ? lotsFor(s, wh, item).find(x => x.lotNo === lot)?.qty : null) || null;
    return [{ ...emptyLine(type), itemId: item, lotNo: type === 'import' ? '' : (lot || AUTO), qty, price: type === 'import' ? itemById(s, item)!.cost : null }];
  }
  return [emptyLine(type)];
}
